// Inference Error Envelope (frontend mirror) — canonical spec lives in
// revolt-site/backend/src/managers/openrouter-manager.js (inference envelope block at top of file).
//
// The backend emits a single, Revolt-namespaced envelope shape for ALL cloud inference
// errors (pre-stream HTTP body, mid-stream SSE sidecar, post-stream abnormal).
// This module mirrors the enum and provides a tolerant `normalizeInferenceError`
// helper that also understands the legacy shapes we are migrating away from
// (INSUFFICIENT_CREDITS, SERVICE_TEMPORARILY_UNAVAILABLE, raw abnormal_termination
// chunks, bare HTTP-status strings).

import { logger } from "../../../core/logger.js";

/**
 * Revolt-namespaced error codes (mirrors backend REVOLT_ERROR_CODES).
 */
export const REVOLT_ERROR_CODES = Object.freeze({
  INSUFFICIENT_CREDITS: "INSUFFICIENT_CREDITS",
  MODEL_RESTRICTED_FREE_PLAN: "MODEL_RESTRICTED_FREE_PLAN",
  SERVICE_TEMPORARILY_UNAVAILABLE: "SERVICE_TEMPORARILY_UNAVAILABLE",
  PROVIDER_ERROR: "PROVIDER_ERROR",
  PROVIDER_RATE_LIMITED: "PROVIDER_RATE_LIMITED",
  MODEL_UNAVAILABLE: "MODEL_UNAVAILABLE",
  AUTHENTICATION_ERROR: "AUTHENTICATION_ERROR",
  NETWORK_ERROR: "NETWORK_ERROR",
  STREAM_INCOMPLETE: "STREAM_INCOMPLETE",
  USAGE_MISSING: "USAGE_MISSING",
  CONTENT_FILTERED: "CONTENT_FILTERED",
  LENGTH_LIMIT: "LENGTH_LIMIT",
  UNKNOWN_ERROR: "UNKNOWN_ERROR",
});

export const ERROR_PHASES = Object.freeze({
  PRE_STREAM: "pre_stream",
  MID_STREAM: "mid_stream",
  POST_STREAM: "post_stream",
});

const FINISH_REASON_TO_CODE = {
  length: REVOLT_ERROR_CODES.LENGTH_LIMIT,
  content_filter: REVOLT_ERROR_CODES.CONTENT_FILTERED,
  error: REVOLT_ERROR_CODES.PROVIDER_ERROR,
};

const DEFAULT_USER_MESSAGE =
  "AI inference service is temporarily experiencing issues. Please try again in a moment.";

/**
 * Normalize anything that looks like an inference error (new envelope, legacy response
 * body, abnormal_termination chunk, raw Error, plain string) into a consistent
 * object shape the UI can rely on.
 *
 * @param {any} raw
 * @returns {{
 *   revolt: {
 *     errorCode: string,
 *     phase: string,
 *     userMessage: string,
 *     retryable: boolean,
 *     incidentType: string|null,
 *   },
 *   provider: { name?: string, httpStatus?: number|null, error?: any } | null,
 *   diagnostics?: Object | null,
 * }}
 */
export function normalizeInferenceError(raw) {
  if (!raw) {
    return _envelope({ revoltCode: REVOLT_ERROR_CODES.UNKNOWN_ERROR });
  }

  // 1) Already new envelope -- trust it.
  if (raw.revolt && typeof raw.revolt === "object" && raw.revolt.errorCode) {
    return {
      revolt: {
        errorCode: raw.revolt.errorCode,
        phase: raw.revolt.phase || ERROR_PHASES.PRE_STREAM,
        userMessage: raw.revolt.userMessage || DEFAULT_USER_MESSAGE,
        retryable: !!raw.revolt.retryable,
        incidentType: raw.revolt.incidentType || null,
      },
      provider: raw.provider || null,
      diagnostics: raw.diagnostics || null,
    };
  }

  // 2) abnormal_termination legacy sidecar chunk.
  if (raw.type === "abnormal_termination" || raw.legacyType === "abnormal_termination") {
    _warnLegacy("abnormal_termination_chunk");
    const finishCode = FINISH_REASON_TO_CODE[raw.finishReason];
    return _envelope({
      revoltCode: finishCode || REVOLT_ERROR_CODES.STREAM_INCOMPLETE,
      userMessage:
        finishCode === REVOLT_ERROR_CODES.LENGTH_LIMIT
          ? "The model reached its maximum output length before finishing."
          : finishCode === REVOLT_ERROR_CODES.CONTENT_FILTERED
          ? "This response was blocked by the provider's content policy."
          : "The connection ended unexpectedly before the reply finished.",
      phase: ERROR_PHASES.POST_STREAM,
      retryable: true,
      diagnostics: {
        terminationReason: raw.terminationReason,
        receivedFinishReason: raw.receivedFinishReason,
        receivedUsage: raw.receivedUsage,
        receivedDone: raw.receivedDone,
        totalChunks: raw.totalChunks,
        finishReason: raw.finishReason,
      },
    });
  }

  // 3) Legacy JSON body (SERVICE_TEMPORARILY_UNAVAILABLE / INSUFFICIENT_CREDITS / etc.)
  if (typeof raw === "object" && (raw.errorCode || raw.code || raw.error)) {
    const code = raw.errorCode || raw.code || null;
    if (code) _warnLegacy(`legacy_body:${code}`);
    const knownCode =
      code && REVOLT_ERROR_CODES[code] ? REVOLT_ERROR_CODES[code] : null;
    return _envelope({
      revoltCode: knownCode || REVOLT_ERROR_CODES.SERVICE_TEMPORARILY_UNAVAILABLE,
      userMessage: raw.error || raw.message || DEFAULT_USER_MESSAGE,
      phase: ERROR_PHASES.PRE_STREAM,
      retryable: true,
      incidentType: raw.incidentType || null,
      provider:
        typeof raw.originalStatus === "number"
          ? {
              name: "openrouter",
              httpStatus: raw.originalStatus,
              error: raw.originalError
                ? { message: raw.originalError, ...(raw.originalErrorDetails || {}) }
                : raw.originalErrorDetails || null,
            }
          : null,
    });
  }

  // 4) Error instance with attached envelope (set by _makeCloudStreamingRequest).
  if (raw instanceof Error) {
    if (raw.revolt && raw.revolt.errorCode) {
      return normalizeInferenceError({
        revolt: raw.revolt,
        provider: raw.provider || null,
      });
    }
    return _envelope({
      revoltCode: raw.errorCode || REVOLT_ERROR_CODES.UNKNOWN_ERROR,
      userMessage: raw.message || DEFAULT_USER_MESSAGE,
    });
  }

  // 5) Plain string.
  if (typeof raw === "string") {
    return _envelope({
      revoltCode: REVOLT_ERROR_CODES.UNKNOWN_ERROR,
      userMessage: raw,
    });
  }

  return _envelope({ revoltCode: REVOLT_ERROR_CODES.UNKNOWN_ERROR });
}

/**
 * Attach a normalized envelope to a new Error instance. Convenient for paths
 * that still throw (e.g. _makeCloudStreamingRequest).
 */
export function errorFromEnvelope(envelope) {
  const normalized = normalizeInferenceError(envelope);
  const err = new Error(normalized.revolt.userMessage);
  err.revolt = normalized.revolt;
  err.provider = normalized.provider;
  err.diagnostics = normalized.diagnostics || null;
  // Legacy mirror for existing consumers.
  err.errorCode = normalized.revolt.errorCode;
  return err;
}

/**
 * Return true when the envelope represents an intentional user cancel. The
 * inference system itself handles cancellation via dedicated paths; this helper
 * exists so error UI never shows a cancel as a "provider outage".
 */
export function isCancellationEnvelope(envelope) {
  if (!envelope) return false;
  const src = envelope.revolt || envelope;
  return (
    src?.errorCode === "CANCELLED" ||
    src?.phase === "cancelled" ||
    envelope?.finishReason === "cancelled"
  );
}

// ---- internals --------------------------------------------------------------

function _envelope({
  revoltCode,
  userMessage = DEFAULT_USER_MESSAGE,
  phase = ERROR_PHASES.PRE_STREAM,
  retryable = false,
  incidentType = null,
  provider = null,
  diagnostics = null,
} = {}) {
  return {
    revolt: {
      errorCode: revoltCode || REVOLT_ERROR_CODES.UNKNOWN_ERROR,
      phase,
      userMessage,
      retryable: !!retryable,
      incidentType: incidentType || null,
    },
    provider: provider || null,
    diagnostics: diagnostics || null,
  };
}

const _legacyWarned = new Set();
function _warnLegacy(key) {
  if (_legacyWarned.has(key)) return;
  _legacyWarned.add(key);
  logger.warn(
    { tags: "ai-inference|error-envelope|legacy", color1: "yellow" },
    `normalizeInferenceError fell back to legacy shape: ${key}. Migrate caller to the new envelope.`
  );
}
